import type { TFunction } from 'i18next';
import { z } from 'zod';
import type { FieldOption, FieldSpec, FormValues } from '@/components/patterns';
import {
  descriptionField,
  edited,
  oneOf,
  optionalText,
  RESOURCE_NAME,
  text,
  withText,
} from '../shared/form-helpers';
import type { WorkspaceInput } from './workspace-api';

/**
 * Formulaire d'un espace de travail (lot L5, revue C13). Pur, testé par `workspace-forms.test.ts`.
 *
 * Types proposés : Jupyter et VS Code (brief `vague-2/espaces-travail.md`), RStudio (option de C13,
 * image avec IRkernel) et SAS Studio (vague 5 du programme, décision « tout terminer »). Seuls les
 * types autres que SAS Studio sont LIVRÉS (`DELIVERED_WORKSPACE_TYPES`) ; SAS Studio porte le badge « bientôt » et
 * reçoit 501 `feature_disabled`, comme un type de job non prouvé. Superset, Metabase, Zeppelin et
 * Grafana, coupés par C13 (501), sont livrés depuis le 2026-10-19 sur le modèle de l'ADR 0028 (l'outil
 * n'admet que le relais de graal-api).
 */
export const WORKSPACE_TYPES = [
  'jupyter',
  'vscode',
  'rstudio',
  'grafana',
  'superset',
  'metabase',
  'zeppelin',
  'sas-studio',
] as const;
export type WorkspaceType = (typeof WORKSPACE_TYPES)[number];
export const DELIVERED_WORKSPACE_TYPES: readonly WorkspaceType[] = [
  'jupyter',
  'vscode',
  'rstudio',
  'grafana',
  'superset',
  'metabase',
  'zeppelin',
];

export const isWorkspaceType = (value: unknown): value is WorkspaceType =>
  WORKSPACE_TYPES.some((type) => type === value);

/**
 * `projects` (#55) : les projets qu'on peut proposer au rattachement. Facultatif : sans lui, pas de
 * champ « Projet » (et l'espace part sans projet, comme avant).
 */
export type WorkspaceCatalogs = {
  instanceTypes: FieldOption[];
  infrastructures: FieldOption[];
  projects?: FieldOption[];
};

/**
 * Valeur « aucun projet » du sélecteur : Radix refuse une option de valeur vide. Elle ne part jamais
 * au serveur — `project_id` est alors ABSENT du corps (création) ou RETIRÉ (édition).
 */
export const NO_PROJECT = 'none';

/** Le champ « Projet », libellé traduit ; `none` en tête. */
function projectField(t: TFunction, projects: FieldOption[]): FieldSpec {
  return {
    name: 'project',
    kind: 'select',
    label: t('admin.workspaces.fields.project'),
    description: t('admin.workspaces.fields.projectHelp'),
    options: [{ value: NO_PROJECT, label: t('admin.workspaces.fields.noProject') }, ...projects],
  };
}

const projectChoice = (projects: FieldOption[] | undefined) =>
  z
    .unknown()
    .optional()
    .refine(
      (value) =>
        value === undefined ||
        value === '' ||
        value === NO_PROJECT ||
        (projects ?? []).some((project) => project.value === value),
      'required',
    );

export function workspaceSchema(t: TFunction, catalogs: WorkspaceCatalogs) {
  return z.object({
    type: oneOf(WORKSPACE_TYPES),
    name: z
      .custom<string>((value) => text(value) !== '', 'required')
      .refine((value) => RESOURCE_NAME.test(text(value)), t('admin.common.errors.resourceName')),
    description: optionalText(),
    instanceType: oneOf(catalogs.instanceTypes.map((option) => option.value)),
    infrastructure: oneOf(catalogs.infrastructures.map((option) => option.value)),
    project: projectChoice(catalogs.projects),
  });
}

/**
 * Une seule infrastructure : elle est prise d'office (brief : « ce champ devrait se figer »). Le
 * projet proposé est `project` (le projet ouvert sur ce poste) s'il est au catalogue, sinon aucun.
 */
export function workspaceDefaults(
  type: WorkspaceType,
  catalogs: WorkspaceCatalogs,
  project?: string,
): FormValues {
  return {
    type,
    name: '',
    description: '',
    instanceType: catalogs.instanceTypes[0]?.value ?? '',
    infrastructure: catalogs.infrastructures.length === 1 ? (catalogs.infrastructures[0]?.value ?? '') : '',
    project: project && catalogs.projects?.some((option) => option.value === project) ? project : NO_PROJECT,
  };
}

export function workspaceFields(t: TFunction, catalogs: WorkspaceCatalogs): FieldSpec[] {
  return [
    {
      name: 'type',
      kind: 'select',
      label: t('admin.workspaces.fields.type'),
      required: true,
      options: WORKSPACE_TYPES.map((type) => ({
        value: type,
        label: DELIVERED_WORKSPACE_TYPES.includes(type)
          ? t(`admin.workspaces.types.${type}`)
          : t('admin.workspaces.soon', { type: t(`admin.workspaces.types.${type}`) }),
      })),
    },
    {
      name: 'name',
      kind: 'text',
      label: t('admin.workspaces.fields.name'),
      description: t('admin.common.resourceNameHelp'),
      required: true,
      placeholder: 'jupyter-conso',
    },
    {
      name: 'instanceType',
      kind: 'select',
      label: t('admin.workspaces.fields.instanceType'),
      required: true,
      options: catalogs.instanceTypes,
    },
    {
      name: 'infrastructure',
      kind: 'select',
      label: t('admin.workspaces.fields.infrastructure'),
      description: t('admin.workspaces.fields.infrastructureHelp'),
      required: true,
      options: catalogs.infrastructures,
    },
    ...(catalogs.projects ? [projectField(t, catalogs.projects)] : []),
    descriptionField(t('admin.workspaces.fields.description')),
  ];
}

const projectOf = (values: FormValues) => {
  const project = text(values['project']);
  return project && project !== NO_PROJECT ? project : '';
};

export function toWorkspacePayload(values: FormValues): WorkspaceInput {
  return {
    name: text(values['name']),
    type: text(values['type']),
    ...withText(values, 'description'),
    instance_type: text(values['instanceType']),
    infrastructure_id: text(values['infrastructure']),
    ...(projectOf(values) ? { project_id: projectOf(values) } : {}),
  };
}

/**
 * Définition : nom, description et projet (#55). Le type ne change pas
 * (`WorkspaceManager#updateWorkspace`). Changer de projet exige `/project/edit` sur le NOUVEAU projet
 * (400 `PROJECT_NOT_FOUND` pour un projet d'un autre locataire) : le serveur tranche.
 */
export function workspaceEditSchema(t: TFunction) {
  return z.object({
    name: z
      .custom<string>((value) => text(value) !== '', 'required')
      .refine((value) => RESOURCE_NAME.test(text(value)), t('admin.common.errors.resourceName')),
    description: optionalText(),
    // Le projet en place peut n'être pas listé (projet illisible pour cet utilisateur) : on ne le
    // juge pas ici, le serveur refuse un projet qu'on ne peut pas modifier.
    project: z.unknown().optional(),
  });
}

/** Le projet en place, même absent de la liste lisible : proposé par son identifiant. */
export const withCurrentProject = (projects: FieldOption[], current: string | undefined): FieldOption[] =>
  current && !projects.some((project) => project.value === current)
    ? [...projects, { value: current, label: current }]
    : projects;

export const workspaceEditFields = (t: TFunction, projects?: FieldOption[]): FieldSpec[] =>
  workspaceFields(t, { instanceTypes: [], infrastructures: [], ...(projects ? { projects } : {}) }).filter(
    (field) => field.name === 'name' || field.name === 'description' || field.name === 'project',
  );

/** Valeurs initiales de la Définition, projet compris (`none` pour un espace libre). */
export const workspaceEditValues = (workspace: {
  name?: string;
  description?: string;
  project_id?: string;
}) => ({
  name: workspace.name ?? '',
  description: workspace.description ?? '',
  project: workspace.project_id || NO_PROJECT,
});

/** JSON Patch : `replace /project_id`, `add` s'il n'y en avait pas, `remove` pour détacher. */
export const toWorkspaceEditPayload = (entry: Record<string, unknown>, values: FormValues) => {
  const next = edited(entry, values, ['name', 'description']);
  if ('project' in values) {
    const project = projectOf(values);
    if (project) next['project_id'] = project;
    else delete next['project_id'];
  }
  return next;
};

/** Dossier de dépôt dans l'espace : chemin absolu sous le home, sans `..`. */
export const UPLOAD_PATH = /^\/(?:[A-Za-z0-9._-]+\/?)*$/u;
export const isUploadPath = (value: string) => UPLOAD_PATH.test(value) && !value.split('/').includes('..');
